import { useClientPortalSelector, selectBooking } from "../../../redux";
import {
    STEP_LOCATION,
    STEP_SERVICE,
    STEP_PROVIDER,
    STEP_AVAILABILITY,
    STEP_REGISTER,
    STEP_CONFIRMATION,
    StepId,
} from "../bookingSlice.types";
import { useAuthorizationContext } from "../../../context";
import { BookingType } from "types/appointmentBooking";

interface IUseEffectiveStepOrderHook {
    effectiveStepOrder: StepId[];
    currentStepIndex: number;
    effectiveCurrentStep: StepId;
}

const getStepOrder = (bookingType: BookingType, loggedIn: boolean): StepId[] => {
    const steps: StepId[] = [STEP_LOCATION];

    if (bookingType === BookingType.CLIENT) {
        steps.push(STEP_SERVICE);
    }

    steps.push(STEP_PROVIDER, STEP_AVAILABILITY);

    if (!loggedIn) {
        steps.push(STEP_REGISTER);
    }

    steps.push(STEP_CONFIRMATION);
    return steps;
};

/**
 * Hook that returns the effective step order based on booking type and login state.
 * When user is logged in, STEP_REGISTER is excluded from the step order.
 * Step order is derived from booking type — not stored in Redux.
 */
export function useEffectiveStepOrder(): IUseEffectiveStepOrderHook {
    const booking = useClientPortalSelector(selectBooking);
    const currentStep = booking?.navigation?.currentStep ?? STEP_LOCATION;
    const bookingType = booking?.session?.bookingType ?? BookingType.CLIENT;
    const { loggedIn } = useAuthorizationContext();

    const effectiveStepOrder = getStepOrder(bookingType, loggedIn);

    // If user is logged in and current step is REGISTER, treat as CONFIRMATION
    const effectiveCurrentStep = loggedIn && currentStep === STEP_REGISTER ? STEP_CONFIRMATION : currentStep;

    const currentStepIndex = effectiveStepOrder.indexOf(effectiveCurrentStep);

    return {
        effectiveStepOrder,
        currentStepIndex,
        effectiveCurrentStep,
    };
}
